
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Mail, User, Briefcase, Send, Shield } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';

// Updated form schema with validation
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Invalid email address" }),
  company: z.string().min(1, { message: "Company name is required" }),
  message: z.string(),
});

const ContactPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  
  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // Prepare email data
    const emailContent = `
      Name: ${values.name}
      Email: ${values.email}
      Company: ${values.company}
      Message: ${values.message}
    `;
    
    // In a real implementation, you would use a backend service or email API
    // to send this to info@noapartners.com
    
    // For demonstration, we'll open the user's email client with this data
    const emailSubject = encodeURIComponent(`Contact Form Submission from ${values.name}`);
    const emailBody = encodeURIComponent(emailContent);
    const mailtoLink = `mailto:info@noapartners.com?subject=${emailSubject}&body=${emailBody}`;
    
    // Open the email client
    window.open(mailtoLink, '_blank');
    
    // Show success message to the user
    toast({
      title: language === 'en' ? "Message prepared!" : "¡Mensaje preparado!",
      description: language === 'en' 
        ? "Your email client has been opened with your message to info@noapartners.com" 
        : "Se ha abierto tu cliente de correo electrónico con tu mensaje para info@noapartners.com"
    });
    
    // Reset the form
    form.reset();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Added more spacing between navbar and content with pt-28 */}
      <main className="pt-28 pb-20">
        {/* Contact Form Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="flex flex-col justify-center">
                <h2 className="font-space-grotesk text-2xl md:text-3xl font-bold mb-6 text-noa-rich-black">
                  {language === 'en' ? 'Contact Information' : 'Información de Contacto'}
                </h2>
                
                <p className="text-noa-charcoal/90 text-lg mb-8">
                  {language === 'en'
                    ? 'Fill out the form or reach out to us directly at:'
                    : 'Completa el formulario o contáctanos directamente en:'}
                </p>
                
                <div className="flex items-center mb-6">
                  <div className="bg-blue-100 p-3 rounded-full mr-4">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-noa-rich-black">Email</h3>
                    <a href="mailto:info@noapartners.com" className="text-blue-600 hover:underline">
                      info@noapartners.com
                    </a>
                  </div>
                </div>
                
                {/* Added confidentiality message */}
                <div className="mt-6 mb-8 p-5 bg-green-50 rounded-lg border border-green-100 flex items-start">
                  <div className="mr-3 mt-0.5">
                    <Shield className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-noa-rich-black mb-1">
                      {language === 'en' ? 'Your Information is Secure' : 'Tu Información está Segura'}
                    </h3>
                    <p className="text-sm text-noa-charcoal/90">
                      {language === 'en' 
                        ? 'We value your time and treat all information shared with us with the highest level of confidentiality. Your details are never shared with third parties without your explicit consent.'
                        : 'Valoramos tu tiempo y tratamos toda la información compartida con nosotros con el más alto nivel de confidencialidad. Tus datos nunca se comparten con terceros sin tu consentimiento explícito.'}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 p-6 bg-blue-50 rounded-lg border border-blue-100">
                  <h3 className="font-space-grotesk font-bold text-lg mb-2 text-noa-rich-black">
                    {language === 'en' ? 'Office Location' : 'Ubicación de la Oficina'}
                  </h3>
                  <address className="not-italic text-noa-charcoal">
                    Guadalajara 21, Roma Nte.,<br />
                    Cuauhtémoc, 06700 Ciudad de México,<br />
                    CDMX, Mexico
                  </address>
                </div>
              </div>
              
              {/* Form */}
              <div className="bg-gray-50 p-6 md:p-8 rounded-lg border border-gray-100">
                <h2 className="font-space-grotesk text-2xl font-bold mb-6 text-noa-rich-black">
                  {language === 'en' ? 'Send us a Message' : 'Envíanos un Mensaje'}
                </h2>
                
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {language === 'en' ? 'Name' : 'Nombre'}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={language === 'en' ? "Your name" : "Tu nombre"}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            {language === 'en' ? 'Email' : 'Correo Electrónico'}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={language === 'en' ? "Your email" : "Tu correo electrónico"}
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            {language === 'en' ? 'Company' : 'Empresa'}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={language === 'en' ? "Your company" : "Tu empresa"}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {language === 'en' ? 'Message' : 'Mensaje'}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={language === 'en' ? "How can we help you?" : "¿Cómo podemos ayudarte?"}
                              className="min-h-[120px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                      <Send className="mr-2 h-4 w-4" />
                      {language === 'en' ? 'Send Message' : 'Enviar Mensaje'}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
